import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	Alert,
	App,
	Avatar,
	Button,
	Divider,
	Empty,
	Input,
	Popconfirm,
	Segmented,
	Select,
	Space,
	Tag,
	Typography,
} from 'antd';

import {
	createGroup,
	deleteGrant,
	deleteGroup,
	getCloneableGroups,
	getGroupMembers,
	getSharing,
	saveGrant,
	updateGrant,
	updateGroup,
} from '../../services/sharing';
import { api } from '../../api/client';
import { API_COLLECTIONS_URL, isAdmin } from '../../utils';
import UserSearchSelect from '../users/UserSearchSelect';
import { userLabel } from '../users/UserChip';
import UserRef from '../users/UserRef';
import InvitedTag from '../users/InvitedTag';
import { CARD_ROLE_OPTIONS, COLLECTION_ROLE_OPTIONS } from '../../utils/permissions';
import { isPreparedInvite } from '../../utils/publicForms';
import { getCollectionFacetCardKeys, getCollectionMemberCardIds } from '../../utils/collections';
import { CardOptionLabel, buildMemberCardOptions } from '../cards/CardVisuals';
import RecordBadge from '../common/RecordBadge';
import Guidance from '../common/Guidance';
import PersonalSharingNudge from './PersonalSharingNudge';
import TabLoadState, { RefreshFailedNotice } from '../common/TabLoadState';
import GroupHeader from '../common/GroupHeader';
import { isPersonal } from '../../utils/collectionKinds';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import ItemFilterBuilder from '../items/ItemFilterBuilder';
import ItemFilterChips from '../items/ItemFilterChips';
import FilterBuilder from '../common/FilterBuilder';
import FilterChips from '../common/FilterChips';
import GroupKindTag from './GroupKindTag';
import ShareLinksPanel from './ShareLinksPanel';
import IntakeFormsSetting from './IntakeFormsSetting';
import LinkedCollectionsPanel from './LinkedCollectionsPanel';
import { filterScopeOption } from './LinkShape';
import { buildFilterFields } from '../../utils/kqlBuilder';
import useAccessTagsField from '../../hooks/useAccessTagsField';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from '../items/QueryRefusalPick';
import { negatedTagWarningText } from '../../utils/tags';
import { cardAndDescendantIds } from '../../utils/facets';
import { collectionCardsPath } from '../../utils/listLinks';
import {
	DYNAMIC_GROUP_MANAGE_LEGACY_NOTE,
	DYNAMIC_GROUP_ROLE_NOTE,
	DYNAMIC_GROUP_SWITCH_NOTE,
	LIVE_RULE_NOTE,
	MEMBERSHIP_OPTIONS,
	NO_IDENTITY_CARD_NOTE,
	SWITCH_TO_DYNAMIC_NOTE,
	SWITCH_TO_STATIC_NOTE,
	dynamicGroupRoleRefusal,
	groupKind,
	identityCardIds,
	isDynamicGroup,
	placeGroupError,
	roleOptionsForGroup,
	ruleOf,
} from '../../utils/dynamicGroups';
import { normalizeId } from '../../utils/ids';
import { lookupsAbove, managesParentOf, parentOf, subCollectionsOf } from '../../utils/collectionFamily';
import { PLUS, CLOSE } from '../icons';
import { confirmDestructive, destructivePopconfirmProps } from '../common/confirmDestructive';
import { withElementChoicesField } from '../../utils/elementRefusal';

const { Text } = Typography;

function GroupMembersAvatars({ members = [] }) {
	if (!members.length) return <Text type="secondary">No members yet</Text>;
	return (
		<Avatar.Group max={{ count: 5 }} size={24}>
			{members.map(member => (
				<span key={normalizeId(member?._id)} title={userLabel(member)}>
					<UserRef user={member} size={24} showName={false} />
				</span>
			))}
		</Avatar.Group>
	);
}

/**
 * Sharing tab (managers only; the tab itself is gated by CollectionForm).
 * Unlike the rest of the settings form, EVERYTHING here applies immediately
 * through the dedicated sharing endpoints — optimistic where safe, always
 * reloading the authoritative view after a mutation, with a message on
 * failure (the FavoritesContext save pattern).
 */
// Sentinel option in the card-scope picker: one click back to whole-collection.
const ALL_CARDS = '__all_cards__';

// `formVolume` (KPR-513): the API has the volume routes, so each intake link
// offers Show volume. `publicForms` (hooks/usePublicForms, read once by the
// settings page): its invite-drafts are the intake forms a package prepared
// and left off (KPR-360), listed under Share links to make a link from.
export default function SharingTab({ collectionId, collection, formVolume = false, reviewRules = false, publicForms = null }) {
	const { message } = App.useApp();
	const [view, setView] = useState(null); // {owner, groups, grants}
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	// The SAVED collection record: the `collection` prop is the settings
	// form's local copy and may hold unsaved Card Library edits, but sharing
	// mutations apply immediately — card scopes must come from server truth.
	const [savedCollection, setSavedCollection] = useState(null);
	// The intake switch as the server last said it. It is an authorization
	// field with its own door, so it is read from the SAVED record and moved
	// only through that door — never through the settings form's copy.
	const [intakeAllowed, setIntakeAllowed] = useState(null);
	// The intake forms a package prepared and left off, not removed.
	const preparedForms = useMemo(
		() => (Array.isArray(publicForms?.data?.forms) ? publicForms.data.forms.filter(isPreparedInvite) : []),
		[publicForms]
	);

	const reload = useCallback(() => {
		if (!collectionId) return Promise.resolve();
		return getSharing(collectionId)
			.then(data => {
				setView(data);
				setLoadError(null);
			})
			.catch(error => setLoadError(error));
	}, [collectionId]);

	useEffect(() => {
		setLoading(true);
		reload().finally(() => setLoading(false));
	}, [reload]);

	useEffect(() => {
		if (!collectionId) return undefined;
		let active = true;
		api.get(`${API_COLLECTIONS_URL}/${collectionId}`)
			.then(({ data }) => {
				if (!active) return;
				setSavedCollection(data);
				setIntakeAllowed(data?.allowIntakeLinks === true);
			})
			.catch(() => {}); // fall back to the prop below
		return () => { active = false; };
	}, [collectionId]);

	// The collection's member cards drive the card-scope picker — from the
	// saved record when available (see above), the prop as a fallback.
	// Membership is two-source (embedded refs + cards OWNED by the
	// collection); definitions supply branding (icon/color) and the parent
	// hierarchy for nesting. Members whose definition isn't in the cache
	// (provider absent in standalone tests, or a ref to a card the caller
	// can't read) fall back to a flat, name-only row from the embedded ref.
	// Optional: the tab degrades gracefully when rendered without the
	// provider.
	const cardDefinitions = useOptionalCardDefinitions();
	const defsById = cardDefinitions?.cardsById;
	const getElementsForCard = cardDefinitions?.getElementsForCard;

	const memberCardIds = useMemo(
		() => getCollectionMemberCardIds(savedCollection || collection, Object.values(defsById || {})),
		[savedCollection, collection, defsById]
	);

	const cardOptions = useMemo(() => {
		const source = savedCollection || collection;
		const refs = Array.isArray(source?.cards) ? source.cards : [];
		const nameByRefId = new Map(refs
			.map(ref => [normalizeId(ref?.cardDefinitionId), ref?.name])
			.filter(([id]) => id));
		const defs = Object.values(defsById || {});
		const fromDefs = buildMemberCardOptions(defs, memberCardIds);
		const covered = new Set(fromDefs.map(opt => opt.value));
		const refOnly = [...memberCardIds]
			.filter(id => !covered.has(id))
			.map(id => ({ value: id, card: { name: nameByRefId.get(id) || 'Untitled card' }, depth: 0 }));
		return [...fromDefs, ...refOnly];
	}, [savedCollection, collection, defsById, memberCardIds]);

	// The filter vocabulary for grant filters and share links: the elements of
	// this collection's member cards, plus the system fields. Managers see the
	// whole collection, so there is no card-scope restriction to apply here.
	// A grant filter and a share link are compiled by the same engine the list
	// is, so they speak the same vocabulary — facets included.
	const facetCardKeys = useMemo(
		() => getCollectionFacetCardKeys(savedCollection || collection),
		[savedCollection, collection]
	);
	// TAGS (T7): what decides who sees what may name the collection's — and
	// its parents' — RESTRICTED tags, by id (the `tags` field in access
	// mode, hooks/useAccessTagsField): an access level's filter, a public
	// link's, a collection link's and a dynamic group's rule alike.
	const accessTagsField = useAccessTagsField(collectionId);
	const filterFields = useMemo(() => [
		// Who-sees-what text: the API resolves its keys strictly (a key two
		// tiers share is refused, never picked), so the builder keeps them.
		...buildFilterFields({
			cards: Object.values(defsById || {}),
			cardIds: memberCardIds.size ? memberCardIds : null,
			getElementsForCard,
			facetCardKeys,
			keepKeys: true,
			noGroups: true,
		}),
		...(accessTagsField ? [accessTagsField] : []),
	], [defsById, memberCardIds, getElementsForCard, facetCardKeys, accessTagsField]);
	// id -> card meta, for branding grant rows anywhere a card is referenced.
	const cardMetaById = useMemo(
		() => new Map(cardOptions.map(opt => [opt.value, opt.card])),
		[cardOptions]
	);

	// A dynamic group's rule may name only a member card whose effective
	// definition (inherited, own and set elements) carries an account link:
	// nobody could ever match a rule on a card that links no record to an
	// account. Same hierarchy grouping as the scope picker, over that subset.
	const identityCardOptions = useMemo(
		() => buildMemberCardOptions(Object.values(defsById || {}), identityCardIds(memberCardIds, getElementsForCard)),
		[defsById, memberCardIds, getElementsForCard]
	);
	// The rule's vocabulary is the rule card's — its elements and its
	// descendants' (an item of a child card IS an item of the parent, which is
	// how the API expands `card = X`), the same fields the items page offers
	// for that card. Memoised per card: every rule row asks for its own.
	const ruleFieldsFor = useMemo(() => {
		const cache = new Map();
		const defs = Object.values(defsById || {});
		return rawId => {
			const id = normalizeId(rawId);
			if (!id) return [];
			if (!cache.has(id)) {
				cache.set(id, [
					...buildFilterFields({
						cards: defs,
						cardIds: new Set(cardAndDescendantIds(defs, id)),
						getElementsForCard,
						facetCardKeys,
						keepKeys: true,
						noGroups: true,
					}),
					...(accessTagsField ? [accessTagsField] : []),
				]);
			}
			return cache.get(id);
		};
	}, [defsById, getElementsForCard, facetCardKeys, accessTagsField]);

	// The same card options and fields, for a link's shape (Linked collections).
	const linkVocabulary = useMemo(
		() => ({ cardOptions, cardMetaById, filterFields }),
		[cardOptions, cardMetaById, filterFields]
	);

	const groups = useMemo(() => view?.groups || [], [view]);
	const grants = view?.grants || [];
	// A SUB-COLLECTION (docs/plans/sub-collections.md §3.2, §5.7): the grants
	// on the collections above it reach it too, so they are part of its
	// audience — shown read-only — and their groups are grantable here.
	const inheritedGrants = useMemo(() => view?.inheritedGrants || [], [view]);
	const inheritedGroups = useMemo(() => view?.inheritedGroups || [], [view]);
	const groupNameById = useMemo(
		() => new Map([...groups, ...inheritedGroups].map(g => [normalizeId(g._id), g.name])),
		[groups, inheritedGroups]
	);
	// The group documents by id — own and inherited — for what depends on a
	// group's kind: a group built from records is never offered manage.
	const groupById = useMemo(
		() => new Map([...groups, ...inheritedGroups].map(g => [normalizeId(g._id), g])),
		[groups, inheritedGroups]
	);
	const familySource = savedCollection || collection;
	const parent = parentOf(familySource);
	const subCollectionCount = subCollectionsOf(familySource).length;
	// The effective cards that point at a record defined above: a reader here
	// opens the records they already point at, never chooses new ones.
	const lookupNotes = useMemo(
		() => lookupsAbove(familySource, memberCardIds, defsById || {}, getElementsForCard),
		[familySource, memberCardIds, defsById, getElementsForCard]
	);

	// `onError(error)` lets a caller place a refusal under the control that
	// caused it (a rule's card or query); it answers true when it did, and the
	// toast is skipped. Either way the view resyncs — the server is
	// authoritative.
	//
	// A saved access query that leaves items out by a restricted tag saves,
	// with the API's warning (T7, ruled d22: `negated_restricted_tag` — a new
	// item is shown until the tag is on it); said once, as given.
	const run = useCallback((mutation, errorText, onError) =>
		mutation()
			.then(answer => {
				const answers = Array.isArray(answer) ? answer : [answer];
				const warning = negatedTagWarningText(answers.flatMap(one => (Array.isArray(one?.warnings) ? one.warnings : [])));
				if (warning) message.warning(warning);
				return reload().then(() => true);
			})
			.catch(error => {
				const handled = typeof onError === 'function' && onError(error) === true;
				if (!handled) {
					// The API's dynamic_group_role refusal reads in the
					// access-level words this tab uses; any other refusal is
					// the API's own sentence.
					const detail = dynamicGroupRoleRefusal(error) || error?.response?.data?.message;
					message.error(detail || errorText);
				}
				return reload().then(() => false);
			}),
	[message, reload]);

	const retryLoad = () => {
		setLoading(true);
		reload().finally(() => setLoading(false));
	};
	if (!view) {
		return (
			<TabLoadState
				loading={loading}
				error={loadError}
				label="sharing"
				errorDetail="Sharing is manager-only. If your access just changed, that may be why."
				onRetry={retryLoad}
			/>
		);
	}

	return (
		<div className="sharing-tab">
			{loadError ? <RefreshFailedNotice label="sharing" onRetry={retryLoad} /> : null}
			{/* The personal collection may be shared, but a collection made for
			    the people and records in mind is the sounder choice — said
			    once, before the first grant, and never as a refusal. */}
			{isPersonal(collection) && grants.length === 0 && groups.length === 0 ? (
				<PersonalSharingNudge />
			) : null}
			<Alert
				type="info"
				showIcon
				className="sharing-tab__notice"
				message="Changes on this tab apply immediately"
				description="Unlike the other settings, sharing changes don't wait for the Save button."
			/>

			<AccessPanel
				collectionId={collectionId}
				owner={view?.owner}
				grants={grants}
				groups={groups}
				groupNameById={groupNameById}
				groupById={groupById}
				cardOptions={cardOptions}
				cardMetaById={cardMetaById}
				filterFields={filterFields}
				run={run}
				parent={parent}
				managesParent={managesParentOf(familySource)}
				inheritedGrants={inheritedGrants}
				inheritedGroups={inheritedGroups}
				subCollectionCount={subCollectionCount}
				lookupNotes={lookupNotes}
			/>

			<Divider />

			{/* Collection links: this collection shared with other collections,
			    and what it reads from them. Its own endpoints, its own reload —
			    the grant list above never carries link rows. */}
			<LinkedCollectionsPanel
				collectionId={collectionId}
				collection={savedCollection || collection}
				vocabulary={linkVocabulary}
			/>

			<Divider />

			<IntakeFormsSetting
				collectionId={collectionId}
				// Strictly `=== true`: absent is off, and so is anything that is
				// not the boolean — the API's own reading.
				allowed={intakeAllowed ?? ((savedCollection || collection)?.allowIntakeLinks === true)}
				onChange={setIntakeAllowed}
			/>

			<Divider />

			<ShareLinksPanel
				collectionId={collectionId}
				filterFields={filterFields}
				allowIntakeLinks={intakeAllowed ?? ((savedCollection || collection)?.allowIntakeLinks === true)}
				cardOptions={cardOptions}
				cardsById={defsById || {}}
				getElementsForCard={getElementsForCard}
				formVolume={formVolume}
				reviewRules={reviewRules}
				preparedForms={preparedForms}
				onPreparedChanged={publicForms?.reload || null}
			/>

			<Divider />

			<GroupsPanel
				collectionId={collectionId}
				groups={groups}
				grants={grants}
				identityCardOptions={identityCardOptions}
				ruleFieldsFor={ruleFieldsFor}
				cardMetaById={cardMetaById}
				run={run}
				parent={inheritedGroups.length ? parent : null}
				subCollectionCount={subCollectionCount}
			/>
		</div>
	);
}

/**
 * "Inherited from Biewend": the grants on the collections above a
 * sub-collection, one group per collection, read-only — they reach this
 * collection by propagation, so they are its audience (owner ruling D13).
 * Changed where they live: the door opens that collection's Sharing tab, for
 * whoever manages it there.
 */
function InheritedGrants({ grants, groupNameById, groupById, cardMetaById, filterFields, managesParent }) {
	const byCollection = new Map();
	grants.forEach(grant => {
		const id = normalizeId(grant.collection?._id);
		if (!id) return;
		if (!byCollection.has(id)) byCollection.set(id, { collection: grant.collection, grants: [] });
		byCollection.get(id).grants.push(grant);
	});
	return [...byCollection.entries()].map(([id, { collection: from, grants: rows }]) => (
		<div key={id} className="sharing-tab__inherited" data-testid="sharing-inherited">
			<GroupHeader
				as="h4"
				label={`Inherited from ${from.name || 'the parent collection'}`}
				count={rows.length}
				countLabel="grant"
			/>
			{rows.map(grant => (
				<InheritedGrantRow
					key={normalizeId(grant._id)}
					grant={grant}
					groupNameById={groupNameById}
					groupById={groupById}
					cardMetaById={cardMetaById}
					filterFields={filterFields}
				/>
			))}
			<p className="sharing-tab__inherited-note meta-line">
				{managesParent
					? <>These reach this collection from {from.name}. <Link to={`/collections/${id}/form?tab=sharing`}>{`Change on ${from.name}`}</Link></>
					: `These reach this collection from ${from.name}. Someone who manages ${from.name} can change them there.`}
			</p>
		</div>
	));
}

function InheritedGrantRow({ grant, groupNameById, groupById = new Map(), cardMetaById, filterFields }) {
	const cardId = grant.card_id ? normalizeId(grant.card_id) : null;
	const roleText = COLLECTION_ROLE_OPTIONS.find(option => option.value === grant.role)?.label || grant.role;
	const group = grant.principalType === 'group' ? groupById.get(normalizeId(grant.principalId)) : null;
	const legacyManage = isDynamicGroup(group) && grant.role === 'manage';
	return (
		<div className="sharing-tab__grant sharing-tab__grant--inherited">
			<div className="sharing-tab__row">
				<div className="sharing-tab__row-principal">
					{grant.principalType === 'group' ? (
						<Space>
							<Tag>Group</Tag>
							<span>{groupNameById.get(normalizeId(grant.principalId)) || 'Deleted group'}</span>
						</Space>
					) : <><UserRef user={grant.user} userId={grant.principalId} /><InvitedTag user={grant.user} /></>}
				</div>
				<div className="sharing-tab__row-scope">
					{cardId ? (
						<RecordBadge kind="card" record={cardMetaById.get(cardId) || null} title={cardMetaById.get(cardId)?.name || 'Card'} size="xs" />
					) : <Text type="secondary">Whole collection</Text>}
				</div>
				<Tag className="sharing-tab__row-role-fixed">{roleText}</Tag>
			</div>
			{legacyManage ? (
				<Text type="secondary" className="small sharing-tab__role-note" data-testid="sharing-dynamic-manage-note">{DYNAMIC_GROUP_MANAGE_LEGACY_NOTE}</Text>
			) : null}
			{grant.query ? (
				<ItemFilterChips readOnly query={grant.query} fields={filterFields} className="sharing-tab__grant-filter-chips" />
			) : null}
		</div>
	);
}

function AccessPanel({
	collectionId, owner, grants, groups, groupNameById, groupById = new Map(), cardOptions, cardMetaById, filterFields, run,
	parent = null, managesParent = false, inheritedGrants = [], inheritedGroups = [], subCollectionCount = 0, lookupNotes = [],
}) {
	const [principalType, setPrincipalType] = useState('user');
	const [principalId, setPrincipalId] = useState(undefined);
	const [role, setRole] = useState('read');
	const [scopeCardIds, setScopeCardIds] = useState([]);
	const [grantQuery, setGrantQuery] = useState('');
	const [adding, setAdding] = useState(false);

	const cardScoped = scopeCardIds.length > 0;
	// A group built from records is never given manage (the API's
	// dynamic_group_role): the picker leaves it out and says why.
	const pickedGroup = principalType === 'group' && principalId ? groupById.get(normalizeId(principalId)) : null;
	const pickedDynamic = isDynamicGroup(pickedGroup);
	const roleOptions = roleOptionsForGroup(cardScoped ? CARD_ROLE_OPTIONS : COLLECTION_ROLE_OPTIONS, pickedGroup);
	// The API's two rules for a filtered grant, enforced here so the affordance
	// never offers a combination the server would 400: read only, and never
	// together with a card scope.
	const filterable = !cardScoped && role === 'read';

	// Card grants and groups built from records carry read/write only — clamp
	// a stale 'manage' selection.
	useEffect(() => {
		if ((cardScoped || pickedDynamic) && role === 'manage') setRole('write');
	}, [cardScoped, pickedDynamic, role]);

	// Leaving the one shape a filter is legal in drops the filter with it —
	// silently carrying it into a write grant is exactly the widening the API
	// refuses.
	useEffect(() => {
		if (!filterable) setGrantQuery('');
	}, [filterable]);

	// The title stays the plain string (it is what the typeahead and the
	// option's hover read); the label adds the kind chip beside it.
	const ownGroupOptions = groups.map(g => {
		const text = `${g.name} (${(g.members || []).length})`;
		return {
			value: normalizeId(g._id),
			title: text,
			label: (
				<span className="sharing-tab__group-option">
					<span>{text}</span>
					<GroupKindTag group={g} tooltip={false} />
				</span>
			),
		};
	});
	// A sub-collection may grant its ancestors' groups too (their members are
	// worked out where the group lives): grouped by collection, each wearing
	// that collection's badge.
	const inheritedGroupOptions = inheritedGroups.map(g => {
		const text = `${g.name} (${Number(g.memberCount) || 0})`;
		return {
			value: normalizeId(g._id),
			title: text,
			label: (
				<span className="sharing-tab__group-option">
					<span>{text}</span>
					<GroupKindTag kind={g.kind} tooltip={false} />
					<RecordBadge kind="collection" record={g.collection} title={g.collection?.name || 'Collection'} size="xs" />
				</span>
			),
		};
	});
	const groupSelectOptions = inheritedGroupOptions.length
		? [
			...(ownGroupOptions.length ? [{ label: 'Groups of this collection', options: ownGroupOptions }] : []),
			{ label: `Groups of ${parent?.name || 'the parent collection'}`, options: inheritedGroupOptions },
		]
		: ownGroupOptions;
	const anyGroups = ownGroupOptions.length + inheritedGroupOptions.length > 0;
	const lookupTargets = [...new Map(lookupNotes.map(note => [normalizeId(note.target?._id), note])).values()];

	// Grouped by itself at the top because it behaves differently from the
	// cards below: picking it RESETS the scope to the whole collection in one
	// click (no unselecting each card by hand). Labels stay plain strings so
	// the closed select's tags render compact; the branded, indented rows
	// come from optionRender (dropdown only). No member cards -> no options
	// at all, so antd's own empty state shows instead of a lone reset row.
	const scopeSelectOptions = useMemo(() => {
		if (!cardOptions.length) return [];
		return [
			{
				label: 'Whole collection',
				options: [{ value: ALL_CARDS, title: 'All cards', label: 'All cards' }],
			},
			{
				label: 'Specific cards',
				options: cardOptions.map(opt => ({
					value: opt.value,
					title: opt.card?.name || 'Untitled card',
					label: opt.card?.name || 'Untitled card',
					card: opt.card,
					depth: opt.depth,
				})),
			},
		];
	}, [cardOptions]);

	const renderScopeOption = option => (
		option.value === ALL_CARDS
			? <Text strong>All cards</Text>
			: <CardOptionLabel card={option.data.card} depth={option.data.depth} />
	);

	const handleScopeChange = values => {
		if (values.includes(ALL_CARDS)) {
			setScopeCardIds([]);
			return;
		}
		setScopeCardIds(values);
	};

	// An element name the filter could mean several of (400
	// `ambiguous_element`, element ids step 3): the person picks one, or any
	// of these cards, and the grant is added again with the pick — the
	// server writes the ids. A new filter starts without picks.
	const grantPicks = useElementChoices(grantQuery || '');
	const resetGrantPicks = grantPicks.reset;
	useEffect(() => { resetGrantPicks(); }, [grantQuery, resetGrantPicks]);

	const submitAdd = async (elementChoices) => {
		if (!principalId) return;
		setAdding(true);
		const scopes = cardScoped ? scopeCardIds : [null];
		const ok = await run(
			() => Promise.all(scopes.map(card_id => saveGrant(collectionId, withElementChoicesField({
				principalType,
				principalId,
				role,
				card_id: card_id || undefined,
				query: filterable ? (grantQuery || undefined) : undefined,
			}, elementChoices)))),
			'Could not add access.',
			grantPicks.take
		);
		// Keep the selections on failure so a partial fan-out can be retried
		// (the reload already shows exactly which grants landed).
		if (ok) {
			setPrincipalId(undefined);
			setScopeCardIds([]);
			setRole('read');
			setGrantQuery('');
		}
		setAdding(false);
	};
	const handleAdd = () => grantPicks.attempt(submitAdd, { text: grantQuery || '' });

	return (
		<section className="sharing-tab__section">
			<h3>Who has access</h3>

			<div className="sharing-tab__row sharing-tab__row--owner">
				<UserRef user={owner} />
				<Tag>Owner</Tag>
			</div>

			{inheritedGrants.length ? (
				<InheritedGrants
					grants={inheritedGrants}
					groupNameById={groupNameById}
					groupById={groupById}
					cardMetaById={cardMetaById}
					filterFields={filterFields}
					managesParent={managesParent}
				/>
			) : null}

			{inheritedGrants.length && grants.length ? (
				<GroupHeader as="h4" label="Granted here" count={grants.length} countLabel="grant" />
			) : null}

			{grants.length === 0 ? (
				<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={inheritedGrants.length ? 'Nothing granted here yet' : 'Not shared with anyone yet'} />
			) : grants.map(grant => (
				<GrantRow
					key={normalizeId(grant._id)}
					collectionId={collectionId}
					grant={grant}
					groupNameById={groupNameById}
					groupById={groupById}
					cardMetaById={cardMetaById}
					filterFields={filterFields}
					run={run}
				/>
			))}

			<Divider plain>Add access</Divider>

			{/* A parent: what is granted here reaches every sub-collection — one
			    plain line, not an alert (§5.7). */}
			{subCollectionCount ? (
				<p className="sharing-tab__family-note meta-line" data-testid="sharing-family-note">
					{`Access granted here also applies to the ${subCollectionCount === 1 ? 'sub-collection' : `${subCollectionCount} sub-collections`}.`}
				</p>
			) : null}

			{/* A sub-collection whose cards point at records above it: a reader
			    here opens the records these items already point at (the
			    referenced-record read), but choosing others needs access there. */}
			{parent && lookupTargets.length ? (
				<Guidance
					title={`People with access here can open the ${parent.name} records these items already point at`}
					why={`To let them choose other ${parent.name} records, grant them access on ${parent.name} for ${lookupTargets.length === 1 ? `the ${lookupTargets[0].target?.name || 'looked-up'} card` : 'those cards'}.`}
					links={['sharing-and-export/share-specific-cards', 'sharing-and-export/share-a-sub-collection']}
					action={managesParent ? { label: `Open ${parent.name}'s sharing`, to: `/collections/${parent._id}/form?tab=sharing` } : null}
					tone="info"
					dismissKey={`sub-collection-lookups:${collectionId}`}
				/>
			) : null}

			<div className="sharing-tab__add">
				<Select
					className="sharing-tab__principal-type"
					value={principalType}
					onChange={value => { setPrincipalType(value); setPrincipalId(undefined); }}
					options={[
						{ value: 'user', label: 'Person' },
						{ value: 'group', label: 'Group' },
					]}
				/>
				{principalType === 'user' ? (
					<UserSearchSelect
						className="sharing-tab__principal"
						value={principalId}
						onChange={setPrincipalId}
						canInvite={isAdmin()}
					/>
				) : (
					<Select
						className="sharing-tab__principal"
						value={principalId}
						onChange={setPrincipalId}
						options={groupSelectOptions}
						// The parent's groups carry its badge: let the list be as
						// wide as its rows, not the narrow select.
						popupMatchSelectWidth={inheritedGroupOptions.length ? false : undefined}
						placeholder={anyGroups ? 'Pick a group' : 'No groups yet — create one below'}
					/>
				)}
				<Select
					className="sharing-tab__scope"
					mode="multiple"
					allowClear
					value={scopeCardIds}
					onChange={handleScopeChange}
					options={scopeSelectOptions}
					optionRender={renderScopeOption}
					filterOption={filterScopeOption}
					placeholder="Whole collection"
					title="Leave empty for the whole collection, or pick specific cards"
				/>
				<Select
					className="sharing-tab__role"
					value={role}
					onChange={setRole}
					options={roleOptions}
				/>
				{/* A filter narrows what a reader can SEE, so it is offered only
				    where the API accepts one: whole-collection, role 'read'. */}
				{filterable ? (
					<span className="sharing-tab__grant-filter">
						<ItemFilterBuilder
							fields={filterFields}
							query={grantQuery}
							onApply={text => setGrantQuery(text || '')}
							// Who-sees-what text is read in every sub-collection
							// too: the server resolves its names, and asks (⚑ K4).
							keepElementNames
						/>
					</span>
				) : null}
				<Button
					type="primary"
					icon={<PLUS />}
					onClick={handleAdd}
					disabled={!principalId || adding}
					loading={adding}
				>
					Add
				</Button>
			</div>

			{pickedDynamic ? (
				<Text type="secondary" className="small sharing-tab__role-note" data-testid="sharing-dynamic-role-note">{DYNAMIC_GROUP_ROLE_NOTE}</Text>
			) : null}

			{filterable && grantQuery ? (
				<ItemFilterChips
					readOnly
					query={grantQuery}
					fields={filterFields}
					className="sharing-tab__grant-filter-chips"
				/>
			) : null}
			<QueryRefusalPick inline refusal={grantPicks.refusal} disabled={adding} onPick={grantPicks.retry} onRepick={grantPicks.repick} access={grantQuery} />
		</section>
	);
}

function GrantRow({ collectionId, grant, groupNameById, groupById = new Map(), cardMetaById, filterFields, run }) {
	const { modal } = App.useApp();
	const grantId = normalizeId(grant._id);
	const cardId = grant.card_id ? normalizeId(grant.card_id) : null;
	const group = grant.principalType === 'group' ? groupById.get(normalizeId(grant.principalId)) : null;
	// A group built from records reads or writes, never manages: the choice
	// leaves manage out, and a grant that holds it from before that rule is
	// shown read-only until the owner's downgrade makes it Can edit.
	const roleOptions = roleOptionsForGroup(cardId ? CARD_ROLE_OPTIONS : COLLECTION_ROLE_OPTIONS, group);
	const legacyManage = isDynamicGroup(group) && grant.role === 'manage';
	// Same two rules as the add form: a filter lives only on a
	// whole-collection read grant.
	const filterable = !cardId && grant.role === 'read';
	// The person's pick for an element the new filter could mean several of.
	const filterPicks = useElementChoices();
	const applyFilter = text => filterPicks.attempt(elementChoices => run(
		() => updateGrant(collectionId, grantId, withElementChoicesField({ role: 'read', query: text || null }, elementChoices)),
		'Could not change the filter.',
		filterPicks.take
	), { text: text || '' });

	const principal = grant.principalType === 'group'
		? (
			<Space>
				<Tag>Group</Tag>
				<span>{groupNameById.get(normalizeId(grant.principalId)) || 'Deleted group'}</span>
				<GroupKindTag group={group} />
			</Space>
		)
		: <><UserRef user={grant.user} userId={grant.principalId} /><InvitedTag user={grant.user} /></>;

	// A stored filter narrows READS. Raising the role would be a 400 on the
	// API rather than a silent widening, so the filter is dropped in the same
	// call that raises it — and because that widens what they see to the
	// whole collection, it is asked first.
	const changeRole = value => {
		const send = () => run(
			() => updateGrant(collectionId, grantId, {
				role: value,
				...(grant.query && value !== 'read' ? { query: null } : {}),
			}),
			'Could not change the role.'
		);
		if (!grant.query || value === 'read') { send(); return; }
		const label = roleOptions.find(option => option.value === value)?.label || value;
		confirmDestructive(modal, {
			title: `Change to ${label} and remove the filter?`,
			content: 'A filter only narrows what a viewer sees, so it is removed. They will see every item in the collection.',
			okText: 'Change role',
			onOk: send,
		});
	};

	return (
		<div className="sharing-tab__grant">
			<div className="sharing-tab__row">
				<div className="sharing-tab__row-principal">{principal}</div>
				<div className="sharing-tab__row-scope">
					{cardId
						? (
							<span title="This grant covers one card (and its child cards)">
								<RecordBadge
									kind="card"
									record={cardMetaById.get(cardId) || null}
									title={cardMetaById.get(cardId)?.name || 'Card'}
									size="xs"
								/>
							</span>
						)
						: <Text type="secondary">Whole collection</Text>}
				</div>
				{filterable ? (
					<span className="sharing-tab__grant-filter">
						<ItemFilterBuilder
							fields={filterFields}
							query={grant.query || ''}
							onApply={applyFilter}
							keepElementNames
						/>
					</span>
				) : null}
				{legacyManage ? (
					<Tag className="sharing-tab__row-role-fixed">
						{COLLECTION_ROLE_OPTIONS.find(option => option.value === 'manage')?.label}
					</Tag>
				) : (
					<Select
						size="small"
						className="sharing-tab__row-role"
						value={grant.role}
						options={roleOptions}
						onChange={changeRole}
					/>
				)}
				<Popconfirm
					title="Remove this access?"
					description="They lose access immediately. History can restore it."
					{...destructivePopconfirmProps('Remove')}
					onConfirm={() => run(
						() => deleteGrant(collectionId, grantId),
						'Could not remove access.'
					)}
				>
					<Button type="text" danger icon={<CLOSE />} aria-label="Remove access" />
				</Popconfirm>
			</div>

			{legacyManage ? (
				<Text type="secondary" className="small sharing-tab__role-note" data-testid="sharing-dynamic-manage-note">{DYNAMIC_GROUP_MANAGE_LEGACY_NOTE}</Text>
			) : null}

			{grant.query ? (
				<ItemFilterChips
					readOnly
					query={grant.query}
					fields={filterFields}
					className="sharing-tab__grant-filter-chips"
				/>
			) : null}
			<QueryRefusalPick inline refusal={filterPicks.refusal} onPick={filterPicks.retry} onRepick={filterPicks.repick} access={filterPicks.text() || ''} />
		</div>
	);
}

// ------------------------------------------------------------------ groups

/**
 * The rule builder's copy. A rule with no conditions is a rule: every record
 * of the card counts, so everyone with a record is a member (the API reads an
 * absent `q` that way). Conditions narrow it — nobody has to write a
 * tautology to mean "everyone".
 */
/** The rule's query a pick was asked for, out of the key its attempt carries (`[card_id, q]`). */
const ruleTextOf = (key) => {
	try { return (JSON.parse(key || 'null') || [])[1] || ''; } catch (err) { return ''; }
};

const RULE_EMPTY_TEXT = 'No conditions: every record of this card counts, so everyone with a record of it is a member. Add conditions to narrow the group.';
const RULE_PLACEHOLDER = 'start-date <= today and (end-date is empty or end-date > today)';

const pluralPeople = n => `${n} ${n === 1 ? 'person' : 'people'}`;

/**
 * The membership switch: People I add (a member list) or Records matching a
 * filter (a rule). One control in the create form and on every row, so the
 * two kinds read as one choice rather than two features.
 */
function MembershipSwitch({ value, onChange, disabled, hint }) {
	return (
		<div className="sharing-tab__membership">
			<Text type="secondary" className="sharing-tab__membership-label">Membership</Text>
			<Segmented
				aria-label="Membership"
				value={value}
				onChange={onChange}
				disabled={disabled}
				options={MEMBERSHIP_OPTIONS}
			/>
			{hint ? <Text type="secondary" className="small">{hint}</Text> : null}
		</div>
	);
}

/**
 * The saved rule in words: the card's badge, then the shared chip describer —
 * or, with no conditions, the sentence that every record of the card counts.
 */
function RuleSummary({ cardId, query, fields, cardMetaById }) {
	const meta = cardMetaById.get(normalizeId(cardId));
	const name = meta?.name || 'Card';
	return (
		<div className="sharing-tab__rule-summary">
			<RecordBadge kind="card" record={meta || null} title={name} size="xs" />
			{(query || '').trim() ? (
				<FilterChips readOnly query={query} fields={fields} className="sharing-tab__rule-chips" />
			) : (
				<Text type="secondary" className="sharing-tab__rule-every">{`Every ${name} record`}</Text>
			)}
		</div>
	);
}

/**
 * The dynamic side of the editor: the card the rule reads (only cards whose
 * effective definition carries an account link), the shared filter builder
 * over that card's vocabulary with `me` refused (a rule has no caller), the
 * drafted rule as chips, and the API's refusals under the control each one
 * names. `roster` (edit only) is the live "Matches N people" line.
 */
function RuleEditor({
	collectionId,
	cardId,
	onCardChange,
	query,
	onQueryChange,
	identityCardOptions,
	ruleFieldsFor,
	cardMetaById,
	errors = {},
	roster = null,
	// An element name the rule could mean several of (element ids step 3),
	// and the person's answer to it.
	elementRefusal = null,
	onElementPick = null,
	onElementRepick = null,
	// The rule text the refusal answers (who-sees-what text: the pick it
	// may take is read from it).
	elementText = '',
}) {
	const fields = useMemo(() => ruleFieldsFor(cardId), [ruleFieldsFor, cardId]);

	if (!identityCardOptions.length) {
		return (
			<Alert
				type="info"
				showIcon
				className="sharing-tab__rule-empty"
				message={NO_IDENTITY_CARD_NOTE}
				action={<Link to={collectionCardsPath(collectionId)}>Open the cards library</Link>}
			/>
		);
	}

	const cardSelectOptions = identityCardOptions.map(opt => ({
		value: opt.value,
		title: opt.card?.name || 'Untitled card',
		label: opt.card?.name || 'Untitled card',
		card: opt.card,
		depth: opt.depth,
	}));

	return (
		<div className="sharing-tab__rule">
			<div className="sharing-tab__rule-row">
				<Select
					className="sharing-tab__rule-card"
					aria-label="Rule card"
					showSearch
					value={cardId || undefined}
					onChange={onCardChange}
					options={cardSelectOptions}
					optionRender={option => <CardOptionLabel card={option.data.card} depth={option.data.depth} />}
					filterOption={filterScopeOption}
					status={errors.card_id ? 'error' : undefined}
					placeholder="Card with the account link"
				/>
				{cardId ? (
					<span className="sharing-tab__grant-filter">
						{/* The builder needs the collection to PICK a lookup value
						    (ItemLookupSelect over the referenced card's records —
						    without it the row is a free-text id box) and to
						    suggest collaborators in the account picker; its
						    recents are the rule's own scope, never the item
						    list's. `disallowMe` leaves out every Me affordance
						    the collection's account link would otherwise offer. */}
						<FilterBuilder
							collectionId={collectionId}
							scopeKey={`group-rule:${collectionId}`}
							fields={fields}
							query={query || ''}
							onApply={text => onQueryChange(text || '')}
							emptyText={RULE_EMPTY_TEXT}
							placeholder={RULE_PLACEHOLDER}
							disallowMe
							// A group's rule decides who sees what, and is read
							// in every sub-collection: the server resolves its
							// names, and asks (⚑ K4).
							keepElementNames
						/>
					</span>
				) : (
					<Text type="secondary">Pick the card the rule reads, then add conditions.</Text>
				)}
			</div>
			{errors.card_id ? <Text type="danger" className="sharing-tab__rule-error" role="alert">{errors.card_id}</Text> : null}
			{cardId ? (
				<RuleSummary cardId={cardId} query={query} fields={fields} cardMetaById={cardMetaById} />
			) : null}
			{errors.q ? <Text type="danger" className="sharing-tab__rule-error" role="alert">{errors.q}</Text> : null}
			<QueryRefusalPick inline refusal={elementRefusal} onPick={value => onElementPick && onElementPick(value)} onRepick={onElementRepick} access={elementText || ''} />
			{roster ? (
				<div className="sharing-tab__rule-roster">
					<Text>{`Matches ${pluralPeople(roster.totalCount)}`}</Text>
					{roster.members.length ? <GroupMembersAvatars members={roster.members} /> : null}
				</div>
			) : null}
			<Text type="secondary" className="sharing-tab__rule-note small">{LIVE_RULE_NOTE}</Text>
		</div>
	);
}

function GroupsPanel({ collectionId, groups, grants, identityCardOptions, ruleFieldsFor, cardMetaById, run, parent = null, subCollectionCount = 0 }) {
	const { message } = App.useApp();
	const [creating, setCreating] = useState(false);
	const [newName, setNewName] = useState('');
	const [newKind, setNewKind] = useState('static');
	const [newMemberIds, setNewMemberIds] = useState([]);
	const [newCardId, setNewCardId] = useState(undefined);
	const [newQuery, setNewQuery] = useState('');
	const [ruleErrors, setRuleErrors] = useState({});
	const [cloneFrom, setCloneFrom] = useState(undefined);
	const [cloneError, setCloneError] = useState(null);
	const [cloneRows, setCloneRows] = useState(null); // null = not loaded yet
	const [cloneLoading, setCloneLoading] = useState(false);

	const loadCloneRows = useCallback(() => {
		if (cloneRows !== null || cloneLoading) return;
		setCloneLoading(true);
		getCloneableGroups()
			.then(list => setCloneRows(list))
			.catch(() => {
				// stay null so reopening the dropdown retries
				message.error('Could not load groups to clone from.');
			})
			.finally(() => setCloneLoading(false));
	}, [cloneRows, cloneLoading, message]);

	// Rows show the kind and the live count; the title stays plain for the
	// hover and the closed select.
	const cloneOptions = useMemo(() => (cloneRows || []).map(g => {
		const text = `${g.name} · ${g.collectionName || 'collection'} (${g.memberCount})`;
		return {
			value: normalizeId(g._id),
			title: text,
			label: (
				<span className="sharing-tab__group-option">
					<span>{text}</span>
					<GroupKindTag kind={groupKind(g)} tooltip={false} />
				</span>
			),
		};
	}), [cloneRows]);

	// A clone keeps its source's kind, so the switch follows the source and
	// the API is not sent a `kind` at all (a stale row could disagree).
	const cloneSource = useMemo(
		() => (cloneFrom ? (cloneRows || []).find(g => normalizeId(g._id) === cloneFrom) || null : null),
		[cloneFrom, cloneRows]
	);
	const effectiveKind = cloneSource ? groupKind(cloneSource) : newKind;
	const dynamic = effectiveKind === 'dynamic';
	// A card alone is a complete rule (every record of it); conditions are optional.
	const ruleComplete = Boolean(newCardId);

	const grantCountFor = groupId =>
		grants.filter(g => g.principalType === 'group' && normalizeId(g.principalId) === groupId).length;

	// The person's pick for an element the rule could mean several of
	// (element ids step 3): the group is created again with it.
	const rulePicks = useElementChoices(JSON.stringify([newCardId || null, newQuery || '']));
	const resetRulePicks = rulePicks.reset;
	useEffect(() => { resetRulePicks(); }, [newQuery, newCardId, resetRulePicks]);

	const submitCreate = async (elementChoices) => {
		const name = newName.trim();
		if (!name) return;
		setCreating(true);
		setRuleErrors({});
		setCloneError(null);
		const ok = await run(
			() => createGroup(collectionId, withElementChoicesField({
				name,
				kind: cloneSource ? undefined : newKind,
				memberIds: dynamic ? undefined : newMemberIds,
				rule: dynamic && !cloneSource ? { card_id: newCardId, q: newQuery } : undefined,
				cloneFromGroupId: cloneFrom,
			}, dynamic && !cloneSource ? elementChoices : null)),
			'Could not create the group.',
			error => {
				// A cloned rule is the source's text: an element it names that
				// this collection does not hold is said by the picker.
				if (!cloneSource && rulePicks.take(error)) return true;
				const placed = placeGroupError(error, 'Could not create the group.');
				// A cloned rule has no editor to sit under: the API's own
				// sentence (its card is not a member here) goes by the picker.
				if (cloneSource && placed.code) { setCloneError(placed.message); return true; }
				if (placed.field) { setRuleErrors({ [placed.field]: placed.message }); return true; }
				return false;
			}
		);
		if (ok) {
			setNewName('');
			setNewKind('static');
			setNewMemberIds([]);
			setNewCardId(undefined);
			setNewQuery('');
			setCloneFrom(undefined);
		}
		setCreating(false);
	};
	const handleCreate = () => rulePicks.attempt(submitCreate, { text: JSON.stringify([newCardId || null, newQuery || '']) });

	const canCreate = Boolean(newName.trim()) && !creating && (!dynamic || cloneSource || ruleComplete);

	return (
		<section className="sharing-tab__section">
			<h3>Groups</h3>
			<Text type="secondary">
				Groups live in this collection. Grant a group access above and every member gets it.
				A group is either the people you add or the people whose records match a filter.
				{parent ? ` ${parent.name}'s groups can be granted here too; they are changed on ${parent.name}.` : ''}
			</Text>

			{groups.length === 0 ? (
				<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No groups yet" />
			) : groups.map(group => (
				<GroupRow
					key={normalizeId(group._id)}
					collectionId={collectionId}
					group={group}
					dependentGrantCount={grantCountFor(normalizeId(group._id))}
					holdsManage={grants.some(g => g.principalType === 'group' && normalizeId(g.principalId) === normalizeId(group._id) && g.role === 'manage')}
					identityCardOptions={identityCardOptions}
					ruleFieldsFor={ruleFieldsFor}
					cardMetaById={cardMetaById}
					run={run}
					subCollectionCount={subCollectionCount}
				/>
			))}

			<Divider plain>New group</Divider>

			<div className="sharing-tab__group-editor sharing-tab__group-editor--new">
				<Input
					className="sharing-tab__group-name"
					placeholder="Group name (e.g. Staff, Family)"
					value={newName}
					onChange={e => setNewName(e.target.value)}
					maxLength={60}
				/>
				<MembershipSwitch
					value={effectiveKind}
					onChange={value => { setNewKind(value); setRuleErrors({}); }}
					disabled={Boolean(cloneSource)}
					hint={cloneSource ? `Follows “${cloneSource.name}”, the group being cloned.` : null}
				/>
				{!dynamic ? (
					<UserSearchSelect
						className="sharing-tab__group-members"
						mode="multiple"
						value={newMemberIds}
						onChange={setNewMemberIds}
						placeholder="Add members"
					/>
				) : cloneSource ? (
					<Text type="secondary">{`The rule is copied from “${cloneSource.name}”.`}</Text>
				) : (
					<RuleEditor
						collectionId={collectionId}
						cardId={newCardId}
						onCardChange={value => { setNewCardId(value); setRuleErrors({}); }}
						query={newQuery}
						onQueryChange={value => { setNewQuery(value); setRuleErrors({}); }}
						identityCardOptions={identityCardOptions}
						ruleFieldsFor={ruleFieldsFor}
						cardMetaById={cardMetaById}
						errors={ruleErrors}
						elementRefusal={rulePicks.refusal}
						onElementPick={rulePicks.retry}
						onElementRepick={rulePicks.repick}
						elementText={ruleTextOf(rulePicks.text())}
					/>
				)}
				<div className="sharing-tab__add sharing-tab__add--group">
					<Select
						className="sharing-tab__group-clone"
						allowClear
						value={cloneFrom}
						onChange={value => { setCloneFrom(value); setCloneError(null); }}
						onDropdownVisibleChange={open => { if (open) loadCloneRows(); }}
						options={cloneOptions}
						loading={cloneLoading}
						placeholder="Clone a group you manage (optional)"
					/>
					<Button
						type="primary"
						icon={<PLUS />}
						onClick={handleCreate}
						disabled={!canCreate}
						loading={creating}
					>
						Create group
					</Button>
				</div>
				{cloneError ? <Text type="danger" className="sharing-tab__rule-error" role="alert">{cloneError}</Text> : null}
			</div>
		</section>
	);
}

function GroupRow({ collectionId, group, dependentGrantCount, holdsManage = false, identityCardOptions, ruleFieldsFor, cardMetaById, run, subCollectionCount = 0 }) {
	const { modal, message } = App.useApp();
	const groupId = normalizeId(group._id);
	const kind = groupKind(group);
	const rule = ruleOf(group);
	const [editing, setEditing] = useState(false);
	const [name, setName] = useState(group.name);
	const [addIds, setAddIds] = useState([]);
	// Switching a static group to a rule is a draft until the rule is saved:
	// the API needs the rule in the same call that switches the kind.
	const [draft, setDraft] = useState(null); // { card_id, q } | null
	const [saving, setSaving] = useState(false);
	const [ruleErrors, setRuleErrors] = useState({});
	const [roster, setRoster] = useState(null); // { members, totalCount } | null

	// Resync when the authoritative name changes (post-reload, or another
	// manager renamed it) — a rejected rename must not linger in the input.
	useEffect(() => { setName(group.name); }, [group.name]);

	// The live roster of a dynamic group, from the members endpoint — the
	// same evaluation access resolution runs — re-read whenever the rule
	// the view carries changes (a save just landed).
	const ruleCardId = rule?.card_id || null;
	const ruleQuery = rule?.q || '';
	useEffect(() => {
		if (kind !== 'dynamic') { setRoster(null); return undefined; }
		let active = true;
		getGroupMembers(collectionId, groupId)
			.then(next => { if (active) setRoster(next); })
			.catch(() => { if (active) setRoster(null); });
		return () => { active = false; };
	}, [collectionId, groupId, kind, ruleCardId, ruleQuery]);

	const memberIds = (group.members || []).map(m => normalizeId(m?._id)).filter(Boolean);

	const commitRename = () => {
		const trimmed = name.trim();
		setEditing(false);
		if (!trimmed || trimmed === group.name) { setName(group.name); return; }
		run(
			() => updateGroup(collectionId, groupId, { name: trimmed }),
			'Could not rename the group.'
		);
	};

	const handleAddMembers = async () => {
		if (!addIds.length) return;
		const ok = await run(
			() => updateGroup(collectionId, groupId, { addMemberIds: addIds }),
			'Could not add members.'
		);
		if (ok) setAddIds([]);
	};

	const removeMember = member => confirmDestructive(modal, {
		title: `Remove ${userLabel(member)} from “${group.name}”?`,
		content: 'They lose whatever access this group gives them on this collection.',
		okText: 'Remove',
		onOk: () => run(
			() => updateGroup(collectionId, groupId, { removeMemberIds: [normalizeId(member?._id)] }),
			'Could not remove the member.'
		),
	});

	const placeErrors = error => {
		const placed = placeGroupError(error);
		if (placed.field) { setRuleErrors({ [placed.field]: placed.message }); return true; }
		return false;
	};

	// A saved rule edits in place, like a grant's filter: every Apply (a
	// Clear included — no conditions is a rule, every record of the card) and
	// every card change is one PUT.
	// The person's pick for an element the rule could mean several of
	// (element ids step 3): the rule is saved again with it.
	const rulePicks = useElementChoices();
	const placeRuleErrors = error => rulePicks.take(error) || placeErrors(error);
	const saveRule = next => {
		setRuleErrors({});
		return rulePicks.attempt(elementChoices => run(
			() => updateGroup(collectionId, groupId, withElementChoicesField({ rule: next }, elementChoices)),
			'Could not change the rule.',
			placeRuleErrors
		), { text: JSON.stringify([next?.card_id || null, next?.q || '']) });
	};

	const handleKindChange = next => {
		if (next === kind && !draft) return;
		if (next === 'static' && draft) { setDraft(null); setRuleErrors({}); return; }
		if (next === 'dynamic') {
			// A group that manages cannot become one built from records (the
			// API refuses it too, for its grants on sub-collections as well).
			if (holdsManage) { message.warning(DYNAMIC_GROUP_SWITCH_NOTE); return; }
			confirmDestructive(modal, {
				title: `Switch “${group.name}” to a rule?`,
				content: SWITCH_TO_DYNAMIC_NOTE,
				okText: 'Switch',
				cancelText: 'Cancel',
				onOk: () => { setDraft({ card_id: undefined, q: '' }); setRuleErrors({}); },
			});
			return;
		}
		confirmDestructive(modal, {
			title: `Switch “${group.name}” to people you add?`,
			content: SWITCH_TO_STATIC_NOTE,
			okText: 'Switch',
			cancelText: 'Cancel',
			onOk: () => run(
				() => updateGroup(collectionId, groupId, { kind: 'static' }),
				'Could not switch the group.'
			),
		});
	};

	const submitDraft = async (elementChoices) => {
		if (!draft?.card_id) return;
		setSaving(true);
		setRuleErrors({});
		const ok = await run(
			() => updateGroup(collectionId, groupId, withElementChoicesField({ kind: 'dynamic', rule: { card_id: draft.card_id, q: draft.q } }, elementChoices)),
			'Could not switch the group.',
			placeRuleErrors
		);
		if (ok) setDraft(null);
		setSaving(false);
	};
	const saveDraft = () => rulePicks.attempt(submitDraft, { text: JSON.stringify([draft?.card_id || null, draft?.q || '']) });

	const shownKind = draft ? 'dynamic' : kind;
	// The head's avatars are the live roster once it has answered; until
	// then the view's own resolved members, which are the same people.
	const headMembers = kind === 'dynamic' && roster ? roster.members : group.members;

	return (
		<div className={`sharing-tab__group${kind === 'dynamic' ? ' sharing-tab__group--dynamic' : ''}`}>
			<div className="sharing-tab__row">
				{editing ? (
					<Input
						size="small"
						className="sharing-tab__group-rename"
						value={name}
						autoFocus
						maxLength={60}
						onChange={e => setName(e.target.value)}
						// Enter just blurs; blur is the single commit path (Enter
						// firing both would send the rename twice). Escape cancels.
						onPressEnter={e => e.target.blur()}
						onKeyDown={e => {
							if (e.key === 'Escape') {
								setName(group.name);
								setEditing(false);
							}
						}}
						onBlur={commitRename}
					/>
				) : (
					<Button type="text" className="sharing-tab__group-title" onClick={() => setEditing(true)} title="Rename group">
						<strong>{group.name}</strong>
					</Button>
				)}
				<GroupKindTag group={group} />
				<GroupMembersAvatars members={headMembers} />
				<Popconfirm
					title={`Delete "${group.name}"?`}
					description={(dependentGrantCount
						? `Its ${dependentGrantCount} access grant${dependentGrantCount === 1 ? '' : 's'} on this collection will be removed too.`
						: 'The group has no access grants on this collection.')
						// A group is grantable on the sub-collections too, and its
						// grants there go with it (keepr-api § Sub-collections).
						+ (subCollectionCount ? ' Any grants to it on the sub-collections are removed as well.' : '')
						+ (dependentGrantCount || subCollectionCount ? ' History can restore the group and its grants.' : ' History can restore it.')}
					{...destructivePopconfirmProps('Delete')}
					onConfirm={() => run(
						() => deleteGroup(collectionId, groupId),
						'Could not delete the group.'
					)}
				>
					<Button type="text" danger icon={<CLOSE />} aria-label={`Delete group ${group.name}`} />
				</Popconfirm>
			</div>

			{kind === 'dynamic' && rule ? (
				<RuleSummary cardId={rule.card_id} query={rule.q} fields={ruleFieldsFor(rule.card_id)} cardMetaById={cardMetaById} />
			) : null}

			<MembershipSwitch value={shownKind} onChange={handleKindChange} />

			{shownKind === 'dynamic' ? (
				<>
					<RuleEditor
						collectionId={collectionId}
						cardId={draft ? draft.card_id : rule?.card_id}
						onCardChange={value => {
							if (draft) { setDraft({ ...draft, card_id: value }); setRuleErrors({}); return; }
							if (value && value !== rule?.card_id) saveRule({ card_id: value, q: rule?.q || '' });
						}}
						query={draft ? draft.q : (rule?.q || '')}
						onQueryChange={value => {
							const next = value || '';
							if (draft) { setDraft({ ...draft, q: next }); setRuleErrors({}); return; }
							if (rule?.card_id && next !== (rule.q || '')) saveRule({ card_id: rule.card_id, q: next });
						}}
						identityCardOptions={identityCardOptions}
						ruleFieldsFor={ruleFieldsFor}
						cardMetaById={cardMetaById}
						errors={ruleErrors}
						roster={draft ? null : roster}
						elementRefusal={rulePicks.refusal}
						onElementPick={rulePicks.retry}
						onElementRepick={rulePicks.repick}
						elementText={ruleTextOf(rulePicks.text())}
					/>
					{draft ? (
						<div className="sharing-tab__rule-actions">
							<Button onClick={() => { setDraft(null); setRuleErrors({}); }}>Cancel</Button>
							<Button type="primary" onClick={saveDraft} disabled={!draft.card_id || saving} loading={saving}>
								Save rule
							</Button>
						</div>
					) : null}
				</>
			) : (
				<>
					<div className="sharing-tab__group-members-list">
						{(group.members || []).map(member => (
							<Tag
								key={normalizeId(member?._id)}
								closable
								onClose={e => { e.preventDefault(); removeMember(member); }}
							>
								{userLabel(member)}
							</Tag>
						))}
					</div>

					<div className="sharing-tab__group-add-members">
						<UserSearchSelect
							mode="multiple"
							value={addIds}
							onChange={setAddIds}
							excludeIds={memberIds}
							placeholder="Add members"
						/>
						<Button onClick={handleAddMembers} disabled={!addIds.length}>Add</Button>
					</div>
				</>
			)}
		</div>
	);
}
